import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterSubscribe({ variant = 'default' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/blog/newsletter/subscribe?email=${encodeURIComponent(email)}`
      );

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || 'Success!');
        // Don't clear email so user can toggle again if needed
        setTimeout(() => {
          setStatus('idle');
          setMessage('');
        }, 3000);
      }
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.detail || 
        'Failed to process request. Please try again later.'
      );
    }
  };

  // Compact variant for footer
  if (variant === 'compact') {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-white">Subscribe to Newsletter</h3>
        <p className="text-sm text-gray-300">
          Get the latest fitness tips and blog posts delivered to your inbox.
        </p>
        
        <form onSubmit={handleSubscribe} className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading' || status === 'success'}
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-300"
          />
          <Button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            variant="secondary"
            className="shrink-0"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>

        {status === 'success' && (
          <div className="flex items-start gap-2 text-sm text-green-400">
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{message}</span>
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-start gap-2 text-sm text-red-400">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{message}</span>
          </div>
        )}
      </div>
    );
  }

  // Default variant - Full section
  return (
    <div className="bg-gradient-to-r from-red-600 to-black rounded-lg p-8 md:p-12">
      <div className="max-w-2xl mx-auto text-center text-white">
        <Mail className="w-12 h-12 mx-auto mb-4" />
        <h2 className="text-3xl font-bold mb-3">Subscribe to Our Newsletter</h2>
        <p className="text-lg text-white/90 mb-6">
          Get the latest fitness tips, workout guides, and blog posts delivered straight to your inbox.
        </p>

        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-4">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading' || status === 'success'}
            className="flex-1 bg-white text-black placeholder:text-gray-500 h-12"
          />
          <Button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            variant="secondary"
            className="h-12 px-8 bg-white text-red-600 hover:bg-gray-100"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>

        {status === 'success' && (
          <div className="flex items-center justify-center gap-2 text-green-200 bg-green-900/30 rounded-md p-3">
            <CheckCircle className="w-5 h-5 shrink-0" />
            <span>{message}</span>
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-center justify-center gap-2 text-red-200 bg-red-900/30 rounded-md p-3">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{message}</span>
          </div>
        )}

        <p className="text-sm text-white/70 mt-4">
          Click Subscribe again with the same email to unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}
